<section class="branding-page brand-edit-page">
  <!-- Top Bar: Standard 76px edge-to-edge header matching Manage Brands Figma -->
  <header class="page-topbar hero-section branding-topbar brand-edit-topbar">
    <div class="topbar-title-block page-header__title">
      <h1 class="page-title">Manage Brands</h1>
      <p class="page-subtitle helper-copy">
        Customize emails and document signing pages with your brand colors, logos, and more.
      </p>
    </div>

    <div class="page-header__actions">
      <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary primary-btn btn-purple"
        (click)="saveChanges()"
        [disabled]="!canSaveGeneral"
      >
        @if (saving) {
          <span class="btn-spinner" aria-hidden="true"></span>
          <span>Saving...</span>
        } @else {
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="4 10 8 14 16 6"></polyline>
          </svg>
          <span>Save changes</span>
        }
      </button>
    </div>
  </header>

  <!-- Page Body with Compact Professional Spacing -->
  <div class="branding-body brand-edit-body">
    <!-- Subheader Row: Back Button + Title matching Figma -->
    <div class="brand-edit-subnav-row">
      <button
        type="button"
        class="brand-edit-back-btn"
        (click)="goBack()"
        aria-label="Back to branding"
        title="Back to branding"
      >
        <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="16" y1="10" x2="4" y2="10"></line>
          <polyline points="10 4 4 10 10 16"></polyline>
        </svg>
      </button>
      <div class="subnav-title-wrap">
        <h2 class="brand-edit-subheading">Edit Brand</h2>
        @if (brand?.name) {
          <span class="brand-edit-subtitle-chip">{{ brand?.name }}</span>
        }
      </div>
    </div>

    <!-- Segmented Tab Strip matching Figma -->
    <div class="tab-list brand-edit-tab-strip" role="tablist" aria-label="Edit brand tabs">
      <button
        type="button"
        class="tab-btn brand-tab-btn"
        role="tab"
        [attr.aria-selected]="activeTab === 'general'"
        [class.is-active]="activeTab === 'general'"
        (click)="selectTab('general')"
      >
        General
      </button>
      <button
        type="button"
        class="tab-btn brand-tab-btn"
        role="tab"
        [attr.aria-selected]="activeTab === 'advanced'"
        [class.is-active]="activeTab === 'advanced'"
        (click)="selectTab('advanced')"
      >
        Advanced
      </button>
      <button
        type="button"
        class="tab-btn brand-tab-btn"
        role="tab"
        [attr.aria-selected]="activeTab === 'email-content'"
        [class.is-active]="activeTab === 'email-content'"
        (click)="selectTab('email-content')"
      >
        Email Content Customization
      </button>
    </div>

    @if (loading) {
      <div class="c-loading-state" aria-live="polite">
        <span class="c-spinner"></span>
        <p class="c-loading-text state-message">Loading brand settings…</p>
      </div>
    } @else if (errorMessage) {
      <div class="alert-banner error" role="alert">
        <p class="state-message state-message--error">{{ errorMessage }}</p>
      </div>
    } @else if (activeTab === 'general') {
      <!-- Tab 1: General Settings Form -->
      <div class="brand-edit-content-wrap">
        <main class="brand-edit-form-card general-form" aria-label="Edit brand form">
          <!-- Section 1: Brand Identity -->
          <section class="form-section form-block form-block--brand">
            <div class="section-title-wrap section-heading section-heading--form">
              <h3 class="form-section-title">Brand Identity</h3>
              <p class="form-section-desc">Set your brand name and make it the default for all documents.</p>
            </div>

            <div class="field-wrap">
              <label class="field-label field-label--large" for="brandName">Brand Name</label>
              <input
                id="brandName"
                type="text"
                class="brand-input"
                [(ngModel)]="draft.name"
                (ngModelChange)="nameErrorMessage = ''"
                placeholder="Brand Name"
              />
              @if (nameErrorMessage) {
                <p class="field-error" role="alert">{{ nameErrorMessage }}</p>
              }
            </div>

            <label class="custom-checkbox-row checkbox-row checkbox-row--simple">
              <input type="checkbox" [(ngModel)]="draft.isDefault" class="brand-checkbox" />
              <div class="checkbox-copy">
                <span class="checkbox-title">Set as default</span>
                <span class="checkbox-desc">Use this brand for all new documents automatically</span>
              </div>
            </label>
          </section>

          <hr class="form-divider" />

          <!-- Section 2: Logo Upload Dropzone -->
          <section class="form-section form-block form-block--logo">
            <div class="section-title-wrap section-heading section-heading--form">
              <h3 class="form-section-title">Logo</h3>
              <p class="form-section-desc">
                Upload your logo by selecting a file from your computer or dragging and dropping it into the box shown
                below. The recommended logo size is <strong>320 x 50 pixels</strong>.
              </p>
            </div>

            <input
              #logoInput
              type="file"
              class="hidden-file sr-only"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              (change)="onLogoSelected($event)"
            />

            <div
              class="logo-dropzone"
              [class.is-drag-active]="dragActive"
              [class.has-preview]="!!draft.logoDataUrl"
              (dragover)="onLogoDragOver($event)"
              (dragleave)="onLogoDragLeave($event)"
              (drop)="onLogoDrop($event)"
            >
              @if (draft.logoDataUrl) {
                <div class="logo-preview-card">
                  <div class="logo-preview-image-wrap">
                    <img [src]="draft.logoDataUrl" [alt]="draft.name || 'Brand logo preview'" />
                  </div>
                  <div class="logo-preview-details logo-preview-copy">
                    <p class="logo-preview-filename">{{ draft.logoFileName || 'Selected logo' }}</p>
                    <button type="button" class="btn-remove-logo link-btn" (click)="clearLogo()">
                      <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <line x1="15" y1="5" x2="5" y2="15"></line>
                        <line x1="5" y1="5" x2="15" y2="15"></line>
                      </svg>
                      <span>Remove logo</span>
                    </button>
                  </div>
                </div>
              } @else {
                <div class="dropzone-empty-content logo-dropzone__empty">
                  <div class="dropzone-icon-circle" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                  </div>
                  <p class="dropzone-prompt">
                    Drop your logo here, or
                    <button type="button" class="link-btn-browse logo-dropzone__browse" (click)="triggerLogoBrowse()">Browse</button>
                  </p>
                  <p class="dropzone-sub logo-dropzone__formats">Supported formats: PNG, JPG or SVG</p>
                </div>
              }
            </div>

            @if (logoErrorMessage) {
              <p class="field-error" role="alert">{{ logoErrorMessage }}</p>
            }
          </section>

          <hr class="form-divider" />

          <!-- Section 3: Email & Signer Page Customization -->
          <section class="form-section form-block form-block--customization">
            <div class="section-title-wrap section-heading section-heading--form">
              <h3 class="form-section-title">Email &amp; Signer page Customization</h3>
            </div>

            <div class="field-wrap">
              <label class="field-label" for="emailDisplayName">Email Display Name</label>
              <p class="field-helper-copy field-helper field-helper--wide">
                You can optionally specify the "From" name that will be shown in emails. The default value uses your
                brand name when no custom display name is provided.
              </p>
              <input
                id="emailDisplayName"
                type="text"
                class="brand-input"
                [(ngModel)]="draft.emailDisplayName"
                placeholder='Enter "From" name'
              />
            </div>
          </section>

          <hr class="form-divider" />

          <!-- Section 4: Brand Colors -->
          <section class="form-section form-block form-block--colors">
            <div class="section-title-wrap section-heading section-heading--form">
              <h3 class="form-section-title">Colors</h3>
              <p class="form-section-desc">Choose your brand colors</p>
            </div>

            <div class="colors-stack color-stack">
              <!-- Color 1: Email Background -->
              <div class="color-picker-row color-row">
                <span class="color-picker-label color-row__label">Email background color</span>
                <div class="color-control-box color-control">
                  <span class="color-swatch-preview color-swatch" [style.background]="draft.emailBackgroundColor" aria-hidden="true"></span>
                  <div class="color-input-shell color-picker-shell">
                    <input type="color" [(ngModel)]="draft.emailBackgroundColor" aria-label="Email background color" />
                    <span class="color-code-display">{{ draft.emailBackgroundColor }}</span>
                    <span class="color-picker-caret" aria-hidden="true">▾</span>
                  </div>
                </div>
              </div>

              <!-- Colors Grid: Button & Button Text -->
              <div class="colors-two-col color-row-group">
                <div class="color-picker-row color-row color-row--compact">
                  <span class="color-picker-label color-row__label">Primary button color</span>
                  <div class="color-control-box color-control">
                    <span class="color-swatch-preview color-swatch" [style.background]="draft.primaryButtonColor" aria-hidden="true"></span>
                    <div class="color-input-shell color-picker-shell">
                      <input type="color" [(ngModel)]="draft.primaryButtonColor" aria-label="Primary button color" />
                      <span class="color-code-display">{{ draft.primaryButtonColor }}</span>
                      <span class="color-picker-caret" aria-hidden="true">▾</span>
                    </div>
                  </div>
                </div>

                <div class="color-picker-row color-row color-row--compact">
                  <span class="color-picker-label color-row__label">Primary button text color</span>
                  <div class="color-control-box color-control">
                    <span
                      class="color-swatch-preview is-bordered color-swatch color-swatch--bordered"
                      [style.background]="draft.primaryButtonTextColor"
                      aria-hidden="true"
                    ></span>
                    <div class="color-input-shell color-picker-shell">
                      <input
                        type="color"
                        [(ngModel)]="draft.primaryButtonTextColor"
                        aria-label="Primary button text color"
                      />
                      <span class="color-code-display">{{ draft.primaryButtonTextColor }}</span>
                      <span class="color-picker-caret" aria-hidden="true">▾</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <hr class="form-divider" />

          <!-- Section 5: Platform Branding Toggle -->
          <section class="form-section form-block form-block--branding">
            <div class="toggle-card-row branding-toggle-row">
              <label class="toggle-switch" aria-label="Show XlhireEsigns Branding">
                <input type="checkbox" [(ngModel)]="draft.showPlatformBranding" />
                <span class="toggle-switch__track" aria-hidden="true">
                  <span class="toggle-switch__thumb"></span>
                </span>
              </label>
              <div class="toggle-copy">
                <span class="toggle-title toggle-row__label">Show XlhireEsigns Branding</span>
                <p class="toggle-desc field-helper">Hide the product footer branding from the previewed email and signer page.</p>
              </div>
            </div>
          </section>

          @if (saveErrorMessage) {
            <div class="alert-banner error" role="alert">
              <p class="state-message state-message--error">{{ saveErrorMessage }}</p>
            </div>
          }

          <!-- Form Actions Footer -->
          <div class="form-actions-footer">
            <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary primary-btn btn-purple"
              (click)="saveChanges()"
              [disabled]="!canSaveGeneral"
            >
              @if (saving) {
                <span class="btn-spinner" aria-hidden="true"></span>
                <span>Saving...</span>
              } @else {
                <span>Save changes</span>
              }
            </button>
          </div>
        </main>
      </div>
    } @else if (activeTab === 'advanced') {
      <!-- Tab 2: Advanced Settings (Signature Frame) -->
      <div class="brand-edit-content-wrap">
        <article class="advanced-settings-card brand-edit-form-card" aria-label="Signature frame settings">
          <header class="advanced-settings-header">
            <div class="toggle-card-row branding-toggle-row">
              <label class="toggle-switch" aria-label="Add a signature frame">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.enabled" />
                <span class="toggle-switch__track" aria-hidden="true">
                  <span class="toggle-switch__thumb"></span>
                </span>
              </label>
              <div class="toggle-copy">
                <span class="toggle-title toggle-row__label">Add a signature frame</span>
                <p class="toggle-desc field-helper">Adds a frame around the signature with the selected details.</p>
              </div>
            </div>
          </header>

          @if (draft.signatureFrame.enabled) {
            <hr class="form-divider" />

            <div class="signature-frame-options" role="group" aria-label="Signature frame detail options">
              <label class="custom-checkbox-row checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showRecipientName" class="brand-checkbox" />
                <span class="checkbox-title">Recipient Name</span>
              </label>
              <label class="custom-checkbox-row checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showRecipientEmail" class="brand-checkbox" />
                <span class="checkbox-title">Recipient Email</span>
              </label>
              <label class="custom-checkbox-row checkbox-row">
                <input type="checkbox" [(ngModel)]="draft.signatureFrame.showTimestamp" class="brand-checkbox" />
                <span class="checkbox-title">Timestamp</span>
              </label>
            </div>
          }

          <div class="signature-frame-preview-card">
            <p class="signature-frame-preview-label">Preview:</p>
            <div class="signature-frame-preview-box signature-frame-preview">
              <p class="signature-frame-preview__timestamp">
                XlhireEsigns@if (draft.signatureFrame.showTimestamp) { · Jul 21, 2026, 06:09 PM }
              </p>
              <div class="signature-frame-preview__body">
                <div class="signature-frame-preview__signature">John Doe</div>
                @if (draft.signatureFrame.showRecipientName) {
                  <p class="signature-frame-preview__meta">John Doe</p>
                }
                @if (draft.signatureFrame.showRecipientEmail) {
                  <p class="signature-frame-preview__meta">johndoe@example.com</p>
                }
                <p class="signature-frame-preview__document-id">1fc5d60f-46ca-41b9-a5d6-fad53595bf43</p>
              </div>
            </div>
          </div>

          @if (saveErrorMessage) {
            <div class="alert-banner error" role="alert">
              <p class="state-message state-message--error">{{ saveErrorMessage }}</p>
            </div>
          }

          <!-- Actions Footer inside Advanced -->
          <div class="form-actions-footer">
            <button type="button" class="btn btn-secondary ghost-btn" (click)="goBack()">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary primary-btn btn-purple"
              (click)="saveChanges()"
              [disabled]="!canSaveGeneral"
            >
              @if (saving) {
                <span class="btn-spinner" aria-hidden="true"></span>
                <span>Saving...</span>
              } @else {
                <span>Save changes</span>
              }
            </button>
          </div>
        </article>
      </div>
    } @else {
      <!-- Tab 3: Email Content Customization -->
      <div class="brand-edit-content-wrap">
        <div class="c-table-card brand-edit-table-card">
          <table class="c-table" aria-label="Email templates table">
            <thead>
              <tr>
                <th class="th-name">EMAIL TEMPLATES</th>
                <th class="th-status">STATUS</th>
                <th class="th-date">LAST MODIFIED</th>
                <th class="actions-th"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              @for (template of templates; track trackByTemplateType($index, template)) {
                <tr class="template-row" (click)="openTemplate(template)">
                  <td class="name-cell">
                    <div class="template-row__main">
                      <div class="template-icon-circle" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                        </svg>
                      </div>
                      <strong class="template-title-text">{{ template.displayName }}</strong>
                    </div>
                  </td>
                  <td class="status-cell">
                    <span
                      class="template-status c-status-pill"
                      [class.is-customized]="template.status === 'Customized'"
                      [class.status-completed]="template.status === 'Customized'"
                    >
                      <span class="c-status-dot"></span>
                      {{ template.status }}
                    </span>
                  </td>
                  <td class="template-modified-cell">
                    <span class="template-modified-date">{{ formatLastModified(template.lastModified) }}</span>
                  </td>
                  <td class="actions-cell">
                    <button type="button" class="template-row__cta action-btn-edit action-btn-outline" (click)="$event.stopPropagation(); openTemplate(template)">
                      <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      <span>Edit</span>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    }
  </div>
</section>
